import { drawStar } from './card-fx'

type Ctx = CanvasRenderingContext2D

export type GlowColors = { gold: string; soft: string }

type Mote = { x: number; y: number; vx: number; vy: number; life: number; age: number; s: number; c: string; r: number }

export type GlowState = {
  motes: Mote[]
  points: { x: number; y: number; s: number; a: number; ph: number }[] | null
  lastPhase: number
}

export type GlowFrame = { back: Ctx; front: Ctx; w: number; h: number; t: number; c: GlowColors; k: number; st: GlowState }

export type GlowDraw = (f: GlowFrame) => void

const fade = (k: number) => (k < 0.2 ? k / 0.2 : 1 - (k - 0.2) / 0.8)

function soft(ctx: Ctx, x: number, y: number, r: number, color: string, alpha: number): void {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, color)
  g.addColorStop(1, 'transparent')
  ctx.globalAlpha = alpha
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
}

function moon(ctx: Ctx, x: number, y: number, r: number): void {
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.arc(x + r * 0.45, y - r * 0.25, r * 0.82, 0, Math.PI * 2, true)
  ctx.fill('evenodd')
}

function orbit(n: number, rx: number, ry: number, speed: number, draw: (ctx: Ctx, x: number, y: number, front: boolean, i: number, f: GlowFrame) => void): GlowDraw {
  return (f) => {
    for (let i = 0; i < n; i++) {
      const a = f.t * speed + (i * 2 * Math.PI) / n
      const x = f.w / 2 + Math.cos(a) * rx * f.k
      const y = f.h / 2 + (Math.sin(a) * ry - 10 + Math.sin(a * 2) * 6) * f.k
      const front = Math.sin(a) > 0
      draw(front ? f.front : f.back, x, y, front, i, f)
    }
  }
}

function motes(f: GlowFrame, rate: number, spawn: () => Mote, step: (m: Mote) => void, paint: (m: Mote, a: number) => void): void {
  if (Math.random() < rate) f.st.motes.push(spawn())
  f.st.motes = f.st.motes.filter((m) => (m.age += 1 / 60) < m.life)
  for (const m of f.st.motes) {
    step(m)
    paint(m, Math.max(0, fade(m.age / m.life)))
  }
}

function scatter(f: GlowFrame, count: number, spreadX: number, spreadY: number, avoid: boolean) {
  if (f.st.points) return f.st.points
  f.st.points = Array.from({ length: count }, () => {
    let x = 0
    let y = 0
    do {
      x = f.w / 2 + (Math.random() - 0.5) * spreadX * f.k
      y = f.h / 2 + (Math.random() - 0.5) * spreadY * f.k
    } while (avoid && Math.abs(x - f.w / 2) < 95 * f.k && Math.abs(y - f.h / 2) < 155 * f.k)
    return { x, y, s: 2 + Math.random() * 4, a: Math.random() * 6.283, ph: Math.random() * 6 }
  })
  return f.st.points
}

const satellites = orbit(3, 125, 38, 0.9, (ctx, x, y, front, i, f) => {
  const s = (front ? 7 : 4.5) * f.k
  ctx.globalAlpha = front ? 1 : 0.55
  ctx.fillStyle = i % 2 ? f.c.soft : f.c.gold
  drawStar(ctx, x, y, s, f.t * 2 + i)
  soft(ctx, x, y, s * 3, f.c.gold, 0.35)
})

const dust: GlowDraw = (f) =>
  motes(
    f,
    0.35,
    () => ({ x: f.w / 2 + (Math.random() - 0.5) * 170 * f.k, y: f.h / 2 + (60 + Math.random() * 80) * f.k, vx: (Math.random() - 0.5) * 8, vy: -(18 + Math.random() * 26) * f.k, life: 3 + Math.random() * 2.5, age: 0, s: 1 + Math.random() * 1.8, c: Math.random() < 0.5 ? f.c.gold : f.c.soft, r: 0 }),
    (m) => {
      m.x += m.vx / 60 + Math.sin(f.t * 2 + m.y * 0.05) * 0.15
      m.y += m.vy / 60
    },
    (m, a) => {
      f.back.globalAlpha = a
      f.back.fillStyle = m.c
      f.back.beginPath()
      f.back.arc(m.x, m.y, m.s, 0, Math.PI * 2)
      f.back.fill()
    },
  )

const fireflies: GlowDraw = (f) => {
  for (const p of scatter(f, 14, 260, 360, false)) {
    p.a += (Math.random() - 0.5) * 0.25
    p.x += Math.cos(p.a) * 0.35
    p.y += Math.sin(p.a) * 0.35
    const dx = p.x - f.w / 2
    const dy = p.y - f.h / 2
    if (Math.abs(dx) > 150 * f.k || Math.abs(dy) > 200 * f.k) p.a = Math.atan2(-dy, -dx)
    const al = 0.35 + 0.65 * Math.max(0, Math.sin(f.t * 1.6 + p.ph))
    soft(f.back, p.x, p.y, 9, f.c.gold, al * 0.45)
    f.back.globalAlpha = al
    f.back.fillStyle = f.c.soft
    f.back.beginPath()
    f.back.arc(p.x, p.y, 1.6, 0, Math.PI * 2)
    f.back.fill()
  }
}

const moons = orbit(2, 118, 44, 0.55, (ctx, x, y, front, i, f) => {
  soft(ctx, x, y, 22 * f.k, f.c.gold, front ? 0.4 : 0.2)
  ctx.globalAlpha = front ? 1 : 0.5
  ctx.fillStyle = i ? f.c.soft : f.c.gold
  moon(ctx, x, y, (front ? 8 : 6) * f.k)
})

const starfall: GlowDraw = (f) =>
  motes(
    f,
    0.06,
    () => ({ x: f.w / 2 + (-40 + Math.random() * 260) * f.k, y: f.h / 2 - (170 - Math.random() * 120) * f.k, vx: -(120 + Math.random() * 60) * f.k, vy: (110 + Math.random() * 50) * f.k, life: 1.1 + Math.random() * 0.6, age: 0, s: 1, c: f.c.soft, r: 0 }),
    (m) => {
      m.x += m.vx / 60
      m.y += m.vy / 60
    },
    (m, a) => {
      f.back.globalAlpha = a
      f.back.strokeStyle = m.c
      f.back.lineWidth = 1.2
      f.back.beginPath()
      f.back.moveTo(m.x, m.y)
      f.back.lineTo(m.x - m.vx * 0.12, m.y - m.vy * 0.12)
      f.back.stroke()
    },
  )

const twinkle: GlowDraw = (f) => {
  for (const p of scatter(f, 22, 290, 380, true)) {
    const al = Math.max(0, Math.sin(f.t * (0.8 + p.s / 4) + p.ph))
    f.back.globalAlpha = al
    f.back.fillStyle = f.c.soft
    drawStar(f.back, p.x, p.y, p.s * (0.6 + al * 0.6))
    soft(f.back, p.x, p.y, p.s * 3, f.c.gold, al * 0.3)
  }
}

const ring: GlowDraw = (f) => {
  for (const p of scatter(f, 120, 0, 0, false)) {
    const r = (110 + (p.ph / 6) * 30) * f.k
    const a = p.a + f.t * 0.35
    const x = f.w / 2 + Math.cos(a) * r
    const y = f.h / 2 + Math.sin(a) * r * 0.28 - Math.cos(a) * r * 0.05
    const front = Math.sin(a) > 0
    const ctx = front ? f.front : f.back
    ctx.globalAlpha = (front ? 0.9 : 0.45) * (0.4 + (p.s / 6) * 0.6)
    ctx.fillStyle = p.s > 4 ? f.c.soft : f.c.gold
    ctx.beginPath()
    ctx.arc(x, y, 0.6 + p.s * 0.22, 0, Math.PI * 2)
    ctx.fill()
  }
}

const pulse: GlowDraw = (f) => {
  const phase = (f.t % 3.2) / 3.2
  if (phase < f.st.lastPhase) {
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.2
      f.st.motes.push({ x: f.w / 2 + (Math.random() - 0.5) * 60 * f.k, y: f.h / 2 - 60 * f.k, vx: Math.cos(a) * 60, vy: Math.sin(a) * 70, life: 1.4, age: 0, s: 3 + Math.random() * 3, c: f.c.soft, r: Math.random() * 3 })
    }
  }
  f.st.lastPhase = phase
  motes(
    f,
    0,
    () => ({ x: 0, y: 0, vx: 0, vy: 0, life: 0, age: 0, s: 0, c: '', r: 0 }),
    (m) => {
      m.x += m.vx / 60
      m.y += m.vy / 60
      m.vy += 0.6
      m.r += 0.03
    },
    (m, a) => {
      f.back.globalAlpha = a
      f.back.fillStyle = m.c
      drawStar(f.back, m.x, m.y, m.s, m.r)
    },
  )
}

export const GLOW_DRAWS: readonly (GlowDraw | null)[] = [null, satellites, dust, null, fireflies, moons, starfall, null, twinkle, ring, pulse]
